import React, { useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useParams } from "react-router-dom";
import Table from "components/table/Table";
import FilterButtons from "components/base/button/FilterButtons";
import SearchInputFeild from "components/base/SearchInputFeild";

import Select from "react-select";
import useFetch from "hooks‬/useFetch";
import Pagination from "components/base/Pagination";
import { getAssignedUsersColumns } from "./AssignedUsersTable.columns";
import { subscriptionApiEndpoint } from "constant/apiConstants";
import {
  allUsersStatus,
  userStatusFilterOptions,
} from "constant/commonConstant";

const AssignedUsersTable: React.FC = () => {
  const { roleGuid } = useParams<{ roleGuid: string }>();
  const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set());

  const {
    data: usersInThisRole,
    isFetchLoading,
    currentPage,
    totalPages,
    cursors,
    isInitialFilterLoadData,
    fetchData: fetchUserHandler,
  } = useFetch<any>({
    url: `${subscriptionApiEndpoint.ENDPOINT_USER_ROLE}/${roleGuid}/users`,
    limit: 15,
    query: "status=" + allUsersStatus,
  });

  // Selection logic
  const isSelected = (guid: string) => selectedGuids.has(guid);
  const toggleSelection = (guid: string) => {
    setSelectedGuids((prev) => {
      const newSet = new Set(prev);
      newSet.has(guid) ? newSet.delete(guid) : newSet.add(guid);
      return newSet;
    });
  };

  const selectAll = () => {
    const allGuids = usersInThisRole.map((u) => u.guid);
    setSelectedGuids(new Set(allGuids));
  };

  const clearAllSelections = () => {
    setSelectedGuids(new Set());
  };

  const columns = getAssignedUsersColumns(
    isSelected,
    toggleSelection,
    selectAll,
    clearAllSelections,
    selectedGuids,
    usersInThisRole
  );

  const [searchValue, setSearchValue] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("A");
  const table = useReactTable({
    data: usersInThisRole,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const handleStatusChange = (selectedOption: any) => {
    setStatusFilter(selectedOption?.value ?? "");
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };
  const filterQueryHandler = async () => {
    const searchFilterParam =
      searchValue.length > 0 ? `&search=${searchValue}` : "";
    let filterQuery = `status=${statusFilter}${searchFilterParam}`;
    fetchUserHandler(undefined, undefined, filterQuery);
  };

  const filterQueryCancelHandler = async () => {
    let filterQuery = `status=${statusFilter} `;
    setSearchValue("");
    fetchUserHandler(undefined, undefined, filterQuery);
  };
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Assigned Users</h5>
        <div className="d-flex gap-2">
          <div className="d-flex gap-4">
            {(isInitialFilterLoadData || usersInThisRole.length > 0) && (
              <>
                <div className="w-125px">
                  <Select
                    defaultValue={{ label: "All", value: allUsersStatus }}
                    className="react-select-styled  p-0 react-select-sm border-select"
                    classNamePrefix="react-select"
                    options={userStatusFilterOptions}
                    onChange={handleStatusChange}
                  />
                </div>
                <div className="w-200px">
                  <SearchInputFeild
                    value={searchValue}
                    onChange={handleSearchChange}
                  />
                </div>
                <FilterButtons
                  filterQueryCancelHandler={filterQueryCancelHandler}
                  filterQueryHandler={() => filterQueryHandler()}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="dataTables_wrapper dt-bootstrap4 no-footer">
        {usersInThisRole?.length > 0 ? (
          <Table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  className="text-start text-muted fw-bold fs-7 text-uppercase gs-0"
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <h1 className="text-center my-5">Data Not Found</h1>
        )}
        <Pagination
          cursors={cursors}
          currentPage={currentPage}
          totalPages={totalPages}
          isLoading={isFetchLoading}
          handlePageChange={fetchUserHandler}
        />
      </div>
      {selectedGuids.size > 0 && (
        <div className="mt-2 d-none text-muted">
          Selected Users: {selectedGuids.size}
        </div>
      )}
    </>
  );
};

export default AssignedUsersTable;
